import { useEffect, useState } from "react";
import { accountTypeHeaders, provincesTaxRates, groupInsuranceCoverageData } from "./quote-utils";

export const QuoteEstimatePerType = ({
    planAccount,
    adminFee,
    province,
    onPercentageUpdate
}) => {
    const [totals, setTotals] = useState({ single: 0, couple: 0, family: 0 });
    const [customPercentage, setCustomPercentage] = useState("");
    const [insurancePremium, setInsurancePremium] = useState(0);

    const percentages = [0.4, 0.65, 0.8, 1.0];
    const accountType = planAccount.accountType;
    const isCoreHealth = ["CoreHealth", "CHFlex", "CHFixed"].includes(accountType);
    const isFixed = accountType === "Fixed" || accountType === "CHFixed";

    useEffect(() => {
        calculateTotals();
    }, [planAccount, adminFee, province]);

    const calculateTotals = () => {
        const newTotals = { single: 0, couple: 0, family: 0 };
        const employeeCounts = { single: 0, couple: 0, family: 0 };

        planAccount.benefitCategories.forEach(category => {
            ["single", "couple", "family"].forEach(level => {
                const limits = category.limits[level];
                const employees = parseInt(limits.noOfEmployees, 10) || 0;
                const limit = parseFloat(limits.limitPerEmployee) || 0;
                const lsaLimit = isFixed ? (parseFloat(limits.limitPerEmployeeLSA) || 0) : 0;

                newTotals[level] += isFixed
                    ? (limit + lsaLimit) * employees
                    : limit * employees;

                if (isCoreHealth && !category.excludeFromCoreHealth) {
                    employeeCounts[level] += employees;
                }
            });
        });

        setTotals(newTotals);
        if (isCoreHealth) {
            calculateInsurancePremium(employeeCounts);
        }
    };

    const calculateInsurancePremium = (employeeCounts) => {
        const rates = { single: 29.99, couple: 39.99, family: 39.99 };
        const multiplier = province === "ON" ? 1.08 : 1;

        const premium = Object.entries(rates).reduce((total, [level, rate]) => {
            return total + (employeeCounts[level] * rate * 12 * multiplier);
        }, 0);

        setInsurancePremium(premium);
    };

    const calculateByPercentage = (percent) => {
        const limitsTotal = Object.values(totals).reduce((sum, val) => sum + (val * percent), 0);
        const adminFeeTotal = limitsTotal * (parseFloat(adminFee) || 0) / 100;
        const tax = adminFeeTotal * (provincesTaxRates[province] || 0);

        let total = limitsTotal + adminFeeTotal + tax;

        if (province === "ON" && accountType !== "LSA") {
            const additionalTax = limitsTotal * 0.08 + (limitsTotal + adminFeeTotal) * 0.02;
            total += additionalTax;
        }

        if (isCoreHealth) {
            total += insurancePremium;
        }

        return {
            limits: {
                single: totals.single * percent,
                couple: totals.couple * percent,
                family: totals.family * percent
            },
            adminFee: adminFeeTotal,
            tax,
            additionalTax: province === "ON" && accountType !== "LSA"
                ? limitsTotal * 0.08 + (limitsTotal + adminFeeTotal) * 0.02
                : 0,
            total
        };
    };

    const handleCustomPercentageChange = (value) => {
        if (value === "") {
            setCustomPercentage("");
            return;
        }

        const num = Number(value);
        if (num < 0 || num > 100) return;

        setCustomPercentage(num);
        onPercentageUpdate(accountType, num);
    };

    const customCalculation = calculateByPercentage(customPercentage / 100);

    const getDescription = (type) => {
        const descriptions = {
            HSA: "HSA",
            LSA: "LSA",
            CoreHealth: "CoreHealth+",
            Flex: "Flexible",
            Fixed: "Fixed",
            CHFlex: "CoreHealth+ Flexible",
            CHF: "CoreHealth+ Flexible",
            CHFixed: "CoreHealth+ Fixed"
        };
        return descriptions[type] || type;
    };

    const renderTaxNotes = () => {
        if (province !== "ON") {
            return <p className="text-xs text-gray-500 mt-2">* {(provincesTaxRates[province] * 100 || 5).toFixed(1)}% provincial GST/HST rate.</p>;
        }

        const notes = [
            isCoreHealth && "* For Ontario, CoreHealth Insurance Premium includes an 8% RST.",
            `${isCoreHealth ? "**" : "*"} ${(provincesTaxRates[province] * 100 || 13).toFixed(1)}% provincial GST/HST rate.`,
            accountType !== "LSA" && `${isCoreHealth ? "***" : "**"} Additional ON tax includes 8% RST & 2% premium tax on the claim amount, 13% HST & 2% premium tax on the admin fee.`,
            "",
            "Note: Calculation includes GST/HST for total dollars spent regardless of HSA or LSA allocation. Provincial taxes not applicable on any LSA reimbursements when the plan goes live. LSA claim amount is exempt from RST and will be reimbursed."
        ].filter(Boolean);

        return <p className="text-xs text-gray-500 mt-2">{notes.map((note, i) => <span key={i}>{note}<br /></span>)}</p>;
    };

    return (
        <div className="mt-4">
            {totals && (
                <div className="bg-white rounded-xl shadow-card p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">{accountTypeHeaders[accountType]}</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="text-left py-2 px-2"></th>
                                    <th className="text-right py-2 px-2 text-xs font-semibold text-gray-500">Typical Usage</th>
                                    <th colSpan="3" className="py-2 px-2"></th>
                                    <th className="text-center py-2 px-2 text-xs font-semibold text-gray-500">Custom % Usage</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                                <tr>
                                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-700">% {getDescription(accountType)} Used</th>
                                    {percentages.map((percent) => (
                                        <th key={percent} className="text-right py-2 px-2 text-sm font-medium text-gray-700">
                                            {(percent * 100).toFixed(0)}%
                                        </th>
                                    ))}
                                    <td className="py-2 px-2 w-24">
                                        <input
                                            type="number"
                                            className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10"
                                            value={customPercentage}
                                            min={0}
                                            max={100}
                                            onWheel={(e) => e.target.blur()}
                                            onChange={(e) => handleCustomPercentageChange(e.target.value)}
                                            placeholder="Enter %"
                                        />
                                    </td>
                                </tr>
                                {["single", "couple", "family"].map((level) => (
                                    <tr key={level} className="hover:bg-gray-50 transition-colors">
                                        <th className="text-left py-2 px-2 text-sm font-medium text-gray-700">{`${getDescription(accountType)} $ Spend (${level.charAt(0).toUpperCase() + level.slice(1)})`}</th>
                                        {percentages.map((percent) => {
                                            const calc = calculateByPercentage(percent);
                                            return (
                                                <td key={percent} className="text-right py-2 px-2 text-gray-600">
                                                    ${calc.limits[level].toLocaleString("en-US", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2
                                                    })}
                                                </td>
                                            );
                                        })}
                                        <td className="text-right py-2 px-2 text-gray-600">
                                            ${customCalculation.limits[level].toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </td>
                                    </tr>
                                ))}
                                {isCoreHealth && (
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <th className="text-left py-2 px-2 text-sm font-medium text-gray-700">Total Core Insurance Premium{province === "ON" && "*"}</th>
                                        {[...percentages, customPercentage].map((_, idx) => (
                                            <td key={idx} className="text-right py-2 px-2 text-gray-600">
                                                ${insurancePremium.toLocaleString("en-US", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </td>
                                        ))}
                                    </tr>
                                )}
                                <tr className="hover:bg-gray-50 transition-colors">
                                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-700">{getDescription(accountType)} Admin Fee</th>
                                    {percentages.map((percent) => (
                                        <td key={percent} className="text-right py-2 px-2 text-gray-600">
                                            ${calculateByPercentage(percent).adminFee.toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </td>
                                    ))}
                                    <td className="text-right py-2 px-2 text-gray-600">
                                        ${customCalculation.adminFee.toLocaleString("en-US", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                </tr>
                                <tr className="hover:bg-gray-50 transition-colors">
                                    <th className="text-left py-2 px-2 text-sm font-medium text-gray-700">GST/HST{isCoreHealth && province === "ON" ? "**" : "*"}</th>
                                    {percentages.map((percent) => (
                                        <td key={percent} className="text-right py-2 px-2 text-gray-600">
                                            ${calculateByPercentage(percent).tax.toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </td>
                                    ))}
                                    <td className="text-right py-2 px-2 text-gray-600">
                                        ${customCalculation.tax.toLocaleString("en-US", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                </tr>
                                {province === "ON" && accountType !== "LSA" && (
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <th className="text-left py-2 px-2 text-sm font-medium text-gray-700">Additional ON Tax{isCoreHealth ? "***" : "**"}</th>
                                        {percentages.map((percent) => (
                                            <td key={percent} className="text-right py-2 px-2 text-gray-600">
                                                ${calculateByPercentage(percent).additionalTax.toLocaleString("en-US", {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2
                                                })}
                                            </td>
                                        ))}
                                        <td className="text-right py-2 px-2 text-gray-600">
                                            ${customCalculation.additionalTax.toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </td>
                                    </tr>
                                )}
                                <tr className="border-t border-gray-200 bg-gray-50">
                                    <th className="text-left py-2 px-2 text-sm font-semibold text-gray-900">Total Cost (Annual Limit)</th>
                                    {percentages.map((percent) => (
                                        <td key={percent} className="text-right py-2 px-2 font-bold text-primary">
                                            ${calculateByPercentage(percent).total.toLocaleString("en-US", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </td>
                                    ))}
                                    <td className="text-right py-2 px-2 font-bold text-primary">
                                        ${customCalculation.total.toLocaleString("en-US", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                        })}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    {renderTaxNotes()}

                    {isCoreHealth && <GroupInsuranceCoverage />}
                </div>
            )}
        </div>
    );
};

const GroupInsuranceCoverage = () => {
    return (
        <div className="mt-4">
            <h4 className="text-md font-semibold text-gray-900 mb-2">Group Insurance Coverage</h4>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="border-b border-gray-100">
                            <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase">Coverage</th>
                            <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase">Single</th>
                            <th className="text-left py-2 px-2 text-xs font-semibold text-gray-500 uppercase">Family</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                        {groupInsuranceCoverageData.map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                                <td className="py-2 px-2 text-gray-700">{item.coverage}</td>
                                <td className="py-2 px-2 text-gray-600">{item.single}</td>
                                <td className="py-2 px-2 text-gray-600">
                                    {item.family.split("\n").map((line, i) => (
                                        <div key={i}>{line}</div>
                                    ))}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
